import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  TrendingUp, ArrowRight, Bike, Dumbbell, Trophy, Activity,
  Heart, CheckCircle2, X, Sparkles, Utensils, MoreVertical,
} from 'lucide-react';
import AIWorkoutModal from '../components/AIWorkoutModal';
import StrengthEntryEditModal from '../components/StrengthEntryEditModal';
import ProfileDepthNudge from '../components/ProfileDepthNudge';
import { isInjuriesProfileComplete } from '../pages/Onboarding';
import { sampleWorkout, getExerciseById } from '../data/exercises';
import { useProfile } from '../context/ProfileContext';
import { recoveryActivities } from '../data/endurance';
import { getProgram, getScheduleForWeek, todayIdx, localISO, getWorkoutHeroImage } from '../data/programs';
import { calcTargets, sumLog, inferNutritionGoal } from '../data/nutrition';
import DailyMotivator from '../components/DailyMotivator';
import MacroNudge from '../components/MacroNudge';
import RollingWindowGoal from '../components/RollingWindowGoal';
import WellbeingCheckIn from '../components/WellbeingCheckIn';
import PacingBanner from '../components/PacingBanner';
import PainCheckCard from '../components/PainCheckCard';
import WelcomeTour from '../components/WelcomeTour';
import TrialBanner from '../components/TrialBanner';
import VerifyEmailBanner from '../components/VerifyEmailBanner';
import ContextualPrompt from '../components/ContextualPrompt';
import WidgetGate from '../components/WidgetGate';
import MacroPattern from '../components/MacroPattern';
import { countPRsInWindow } from '../utils/training-analytics';
import BreathingFlameMark from '../components/BreathingFlameMark';
import IncompleteProfileHero from '../components/IncompleteProfileHero';
import { Settings, User, ArrowUpRight, Heart as HeartIcon, Utensils as UtensilsIcon, Swords, Sparkles as SparklesIcon, BookOpen as BookOpenIcon, Lock } from 'lucide-react';
import { WIDGET_CATALOG, isModuleVisible } from '../data/displayPrefs';
import { useFeatureFlag } from '../context/FeatureFlagsContext';
import { useTierState } from '../hooks/useTierState';
import PaywallModal from '../components/PaywallModal';

const Stat = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="relative bg-enf-surface-deep border border-enf-surface-border rounded-xl p-5 overflow-hidden">
    {accent && <div className="absolute top-0 left-4 right-4 h-px iron-rule" />}
    <div className="flex items-center justify-between mb-3">
      <div className="w-9 h-9 rounded-md bg-enf-surface-mid flex items-center justify-center">
        <Icon className="w-4 h-4 text-enf-accent-fg" />
      </div>
      <span className="text-xs text-enf-text-muted">{sub}</span>
    </div>
    <div className="font-display text-4xl tracking-wide leading-none">{value}</div>
    <div className="text-xs text-enf-text-muted uppercase tracking-wider mt-2">{label}</div>
  </div>
);

// Compute Monday-anchored start of the current week
const startOfWeek = (d = new Date()) => {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7; // Mon=0
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - day);
  return date;
};

// Categorize a history entry. Strength workouts have exercises[]; recovery sessions usually carry recoveryId or activityType.
const isRideOrRecovery = (h) => {
  if (h.activityType && h.activityType !== 'strength') return true;
  if (h.recoveryId) return true;
  // No exercises array or 0 strength exercises => treat as cardio/recovery
  return !h.exercises || h.exercises.length === 0;
};
const isStrengthWorkout = (h) => !isRideOrRecovery(h);

const Dashboard = () => {
  const { profile, history, training, update, updateWorkout, removeWorkout } = useProfile();
  const { is_paid: isPaid } = useTierState();
  const [nutritionPaywallOpen, setNutritionPaywallOpen] = useState(false);
  const [aiPaywallOpen, setAiPaywallOpen] = useState(false);
  const [params] = useSearchParams();
  const showCelebration = params.get('completed') === '1';
  const [showBanner, setShowBanner] = useState(showCelebration);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);

  const lastWorkout = history[0];
  const firstName = (profile.name || 'Athlete').split(' ')[0];

  // ----- Real metrics from history -----
  const totalStrength = useMemo(() => history.filter(isStrengthWorkout).length, [history]);
  const totalRides = useMemo(() => history.filter(isRideOrRecovery).length, [history]);

  const weekStart = useMemo(() => startOfWeek(), []);
  const thisWeekHistory = useMemo(() => history.filter((h) => new Date(h.date) >= weekStart), [history, weekStart]);
  const thisWeekSessions = thisWeekHistory.length;
  const goal = profile.daysPerWeek || 4;

  // Combatives sessions surfaced on Progress as their own series alongside
  // strength and cardio. Sessions live on `training.sessions` (martial arts
  // tracking) — separate collection from workouts so we union them into the
  // weekly chart explicitly. Color = enf-role-warning (warm) — distinct
  // from strength (accent) and cardio (info)..
  const combativesSessions = useMemo(() => {
    // Newest first — same guarantee Strength workouts get. Without an
    // explicit sort here, cloud-hydrate / sync merges can shuffle order.
    const raw = training?.sessions || [];
    return raw.slice().sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [training]);

  // Trend overlay: compute prior week's total minutes and 4-week rolling average for the "This Week" chart
  const trendStats = useMemo(() => {
    const now = new Date();
    const weekMillis = 7 * 24 * 60 * 60 * 1000;
    const weeklyTotals = [0, 0, 0, 0, 0]; // [thisWeek, lastWeek, 2wkAgo, 3wkAgo, 4wkAgo]
    const tally = (entries, durKey = 'duration') => entries.forEach((h) => {
      const d = new Date(h.date);
      const ws = startOfWeek(now);
      const diffWeeks = Math.floor((ws - startOfWeek(d)) / weekMillis);
      if (diffWeeks >= 0 && diffWeeks <= 4) {
        weeklyTotals[diffWeeks] += h[durKey] || 0;
      }
    });
    tally(history);
    tally(combativesSessions);
    const lastWeek = weeklyTotals[1];
    const prior4Weeks = weeklyTotals.slice(1, 5);
    const trainingWeeks = prior4Weeks.filter((v) => v > 0).length;
    const avg4wk = trainingWeeks > 0 ? Math.round(prior4Weeks.reduce((a, b) => a + b, 0) / trainingWeeks) : 0;
    return { lastWeek, avg4wk };
  }, [history, combativesSessions]);

  // PRs Hit (last 30 days). Computes on the fly from history — no schema
  // change required and stays in sync if entries are edited / deleted.
  // See countPRsInWindow() for the math (Epley e1RM, ≥1 lb strictly improved
  // over the same exercise's prior-history best).
  const prsThisMonth = useMemo(() => countPRsInWindow(history, 30), [history]);

  // Streak removed — replaced by RollingWindowGoal (Anti-Shame UX, Phase 0)

  // Weekly chart bars: minutes per day (Mon→Sun), split by strength/cardio/combatives
  const weeklyBars = useMemo(() => {
    const bars = Array.from({ length: 7 }, (_, i) => ({
      idx: i,
      day: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
      strength: 0,
      cardio: 0,
      combatives: 0,
    }));
    thisWeekHistory.forEach((h) => {
      const d = new Date(h.date);
      const i = (d.getDay() + 6) % 7;
      const minutes = h.duration || 0;
      if (isRideOrRecovery(h)) bars[i].cardio += minutes;
      else bars[i].strength += minutes;
    });
    // Combatives sessions from training.sessions
    combativesSessions.forEach((s) => {
      const d = new Date(s.date);
      if (d < weekStart) return;
      const i = (d.getDay() + 6) % 7;
      bars[i].combatives += s.duration || 0;
    });
    return bars;
  }, [thisWeekHistory, combativesSessions, weekStart]);
  const totalWeekMinutes = weeklyBars.reduce((sum, b) => sum + b.strength + b.cardio + b.combatives, 0);
  const combativesThisWeek = weeklyBars.reduce((sum, b) => sum + b.combatives, 0);

  // Resolve today's slot: use stored schedule or program default
  const programType = profile.programType || (profile.noEquipment ? 'calisthenics' : 'upper-lower');
  const program = getProgram(programType);
  const hasRecovery = (profile.recoveryActivities || []).length > 0;
  const schedule = Array.isArray(profile.schedule) && profile.schedule.length === 7
    ? profile.schedule
    : getScheduleForWeek({
        programId: programType,
        experience: profile.experience,
        daysPerWeek: profile.daysPerWeek || 4,
        startDate: profile.startDate || localISO(new Date()),
        hasRecovery,
        viewDate: new Date(),
      });
  const todaySlot = schedule[todayIdx()];
  const isRestDay = todaySlot === 'rest' || todaySlot === 'recovery';
  const userRecActs = (profile.recoveryActivities || []).map((id) => recoveryActivities.find((a) => a.id === id)).filter(Boolean);
  const recPick = userRecActs[0] || recoveryActivities[0];
  const recPickWorkouts = recPick?.workouts
    || recPick?.modes?.indoor?.surfaces?.[0]?.workouts
    || recPick?.modes?.outdoor?.surfaces?.[0]?.workouts
    || [];
  const recWorkout = recPickWorkouts.find((w) => w.level === (profile.experience || 'beginner')) || recPickWorkouts[0];
  const todayTemplate = !isRestDay && program.templates[todaySlot];
  const tierLimit = profile.minutesPerSession === 30 ? 1 : profile.minutesPerSession === 45 ? 2 : 3;
  const todayBlocks = todayTemplate
    ? todayTemplate.blocks.filter((b) => { const ex = getExerciseById(b.refId); return ex && ex.tier <= tierLimit; })
    : sampleWorkout.blocks.filter((b) => { const ex = getExerciseById(b.refId); return ex && ex.tier <= tierLimit; });

  // ----- Today's fuel (nutrition snapshot) -----
  const hasNutritionBasics = !!(profile.weight && profile.age && profile.sex && profile.heightFt);
  const todayIso = localISO(new Date());
  const nutritionTargets = useMemo(() => {
    if (!hasNutritionBasics) return null;
    const goalId = profile.nutritionGoal || inferNutritionGoal(profile.goals || []);
    return calcTargets({ profile, goalId, activityId: profile.activityLevel || 'moderate' });
  }, [profile, hasNutritionBasics]);
  const todayFoodLog = useMemo(
    () => (profile.foodLog && profile.foodLog[todayIso]) || [],
    [profile.foodLog, todayIso]
  );
  const nutritionTotals = useMemo(() => sumLog(todayFoodLog), [todayFoodLog]);
  const kcalPct = nutritionTargets ? Math.min(100, Math.round((nutritionTotals.kcal / nutritionTargets.calories) * 100)) : 0;
  const proteinPct = nutritionTargets ? Math.min(100, Math.round((nutritionTotals.protein / nutritionTargets.proteinG) * 100)) : 0;
  const carbsPct = nutritionTargets ? Math.min(100, Math.round((nutritionTotals.carbs / nutritionTargets.carbG) * 100)) : 0;
  const fatPct = nutritionTargets ? Math.min(100, Math.round((nutritionTotals.fat / nutritionTargets.fatG) * 100)) : 0;

  return (
    <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16">
      <div className="max-w-7xl mx-auto px-6">
        <TrialBanner />
        <VerifyEmailBanner />

        {!profile.completedOnboarding && (
          <IncompleteProfileHero firstName={profile.name ? profile.name.split(' ')[0] : ''} />
        )}

        <ProfileDepthNudge />

        <ContextualPrompt
          id="injuries-first-progress-visit"
          visible={!isInjuriesProfileComplete(profile)}
          title="One quick thing about your body"
          body="We haven't asked about injuries or limitations yet — sharing them lets the coach train around them, not into them."
          action={{ label: 'Tell the coach', href: '/onboarding/full#injuries' }}
          dismissLabel="No injuries"
          onDismiss={() => update({ noInjuries: true, injuries: {}, injuryNotes: '' })}
          testid="prompt-injuries"
        />

        {/* Celebration banner */}
        {showBanner && lastWorkout && (
          <div className="mb-6 relative overflow-hidden rounded-xl border border-enf-border-special/40 bg-gradient-to-r from-enf-surface-special via-enf-border-special to-enf-accent p-1">
            <div className="bg-enf-surface-deep/30 backdrop-blur-sm rounded-lg px-6 py-5 flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-enf-accent flex items-center justify-center flex-shrink-0">
                <Trophy className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-[0.3em] text-enf-role-info font-semibold mb-1">Workout logged</div>
                <div className="font-display text-3xl tracking-wide leading-none mb-2">Tempered. {firstName}, you finished {lastWorkout.title}.</div>
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-enf-role-info" /> {lastWorkout.exercisesCompleted ?? 0}/{lastWorkout.exerciseCount ?? 0} exercises</div>
                  <div className="flex items-center gap-1.5"><Activity className="w-4 h-4 text-enf-role-info" /> {(lastWorkout.totalVolume ?? 0).toLocaleString()} lb volume</div>
                  <div className="flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-enf-role-info" /> Logged · added to your weekly pattern</div>
                </div>
              </div>
              <button onClick={() => setShowBanner(false)} className="text-enf-text-primary/70 hover:text-enf-text-primary">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <div className="text-enf-role-info text-xs uppercase tracking-[0.3em] font-semibold mb-2 flex items-center gap-3">
              <span>Welcome back</span>
              <Link
                to="/profile/display"
                className="hidden md:inline-flex items-center gap-1 text-enf-text-muted hover:text-enf-role-info transition-colors normal-case tracking-normal font-normal text-[11px]"
                data-testid="dashboard-display-prefs-link"
              >
                <Settings className="w-3 h-3" /> Customize what shows here
              </Link>
            </div>
            <h1 className="font-display text-6xl md:text-8xl tracking-wide leading-none">{firstName}.</h1>
          </div>
          <RollingWindowGoal history={history} goalDaysPerWeek={profile.daysPerWeek || 4} />
        </div>

        {/* Profile + Settings entry — replaces the old red avatar in the navbar.
            Slim header card so it doesn't crowd the page. */}
        <Link
          to="/profile"
          className="mb-6 flex items-center justify-between gap-3 px-4 py-3 bg-enf-surface-deep border border-enf-surface-border hover:border-enf-accent-fg/40 rounded-xl group transition-colors"
          data-testid="progress-profile-card"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full bg-enf-accent/15 border border-enf-accent-fg/40 flex items-center justify-center text-enf-accent-fg font-bold text-sm flex-shrink-0">
              {(firstName?.[0] || 'E').toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-enf-text-primary truncate">{profile.name || 'Profile & settings'}</div>
              <div className="text-[11px] text-enf-text-muted truncate">Edit your profile, equipment, injuries, schedule, display preferences</div>
            </div>
          </div>
          <ArrowUpRight className="w-4 h-4 text-enf-text-muted group-hover:text-enf-accent-fg transition-colors flex-shrink-0" />
        </Link>

        {/* CI-7: PacingBanner now shows in Athlete Mode too
            (feature-parity rule). Still behind the pacingBanner WidgetGate so
            users can hide it in Display Preferences. */}
        <WidgetGate page="dashboard" id="pacingBanner" className="mb-6">
          <div data-testid="dashboard-pacing-banner">
            <PacingBanner />
          </div>
        </WidgetGate>

        <div className="mb-10 space-y-4">
          <WidgetGate page="dashboard" id="macroNudge">
            {isPaid && <MacroNudge workoutTitle={todayTemplate?.title} isRestDay={isRestDay} />}
          </WidgetGate>
          <WidgetGate page="dashboard" id="dailyMotivator">
            <DailyMotivator />
          </WidgetGate>
        </div>

        <WidgetGate page="dashboard" id="wellbeing" className="mb-10">
          <div data-testid="dashboard-wellbeing">
            <WellbeingCheckIn />
          </div>
        </WidgetGate>

        <WidgetGate page="dashboard" id="painCheck" className="mb-10">
          <div data-testid="dashboard-pain">
            <PainCheckCard />
          </div>
        </WidgetGate>

        <WidgetGate page="dashboard" id="stats" className="mb-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat icon={Dumbbell} label="Workouts" value={totalStrength} sub="All time" accent />
            <Stat icon={Bike} label="Endurance & Adventures" value={totalRides} sub="All time" />
            <Stat icon={TrendingUp} label="Weekly Goal" value={`${Math.min(thisWeekSessions, goal)}/${goal}`} sub="Sessions" accent />
            <Stat icon={Trophy} label="PRs Hit" value={prsThisMonth === 0 ? '—' : prsThisMonth} sub="This month" />
          </div>
        </WidgetGate>

        <div className="grid lg:grid-cols-3 gap-5">
          {/* Today */}
          {isRestDay && recPick ? (
            <Link to="/endurance" className="lg:col-span-2 group bg-enf-surface-deep border border-enf-surface-border rounded-xl overflow-hidden hover:border-enf-accent-fg/50 transition-colors">
              <div className="relative aspect-[16/7]">
                <img src={recPick.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="text-xs uppercase tracking-[0.3em] text-enf-role-info font-semibold mb-2 flex items-center gap-2"><Heart className="w-3.5 h-3.5" /> Endurance Day</div>
                  <h3 className="font-display text-4xl md:text-5xl tracking-wide leading-none mb-3">{recPick.name}{recWorkout ? `: ${recWorkout.name}` : ''}</h3>
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    {recWorkout && <span className="px-2 py-1 rounded bg-enf-surface-elevated border border-enf-surface-border text-enf-text-secondary">{recWorkout.duration} min</span>}
                    {recWorkout && <span className="px-2 py-1 rounded bg-enf-surface-elevated border border-enf-surface-border text-enf-text-secondary capitalize">{recWorkout.level}</span>}
                    <span className="px-2 py-1 rounded bg-enf-surface-elevated border border-enf-surface-border text-enf-text-secondary">Low intensity</span>
                  </div>
                </div>
              </div>
              <div className="p-6 flex flex-wrap items-center justify-between gap-4">
                <p className="text-sm text-enf-text-secondary max-w-md">{recWorkout?.detail || 'Choose your activity and dial in your intensity.'}</p>
                <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-enf-accent group-hover:bg-enf-accent-hover text-enf-accent-contrast rounded-md font-semibold text-sm transition-colors">
                  Start endurance <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          ) : (
            <div className="lg:col-span-2 bg-enf-surface-deep border border-enf-surface-border rounded-xl overflow-hidden">
              <div className="relative aspect-[16/7]">
                <img
                  src={getWorkoutHeroImage(todayTemplate?.title || sampleWorkout.title)}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover opacity-70"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="text-xs uppercase tracking-[0.3em] text-enf-role-info font-semibold mb-2">Today · {program.name}</div>
                  <h3 className="font-display text-4xl md:text-5xl tracking-wide leading-none mb-3">{todayTemplate?.title || sampleWorkout.title}</h3>
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="px-2 py-1 rounded bg-enf-surface-elevated border border-enf-surface-border text-enf-text-secondary">{profile.minutesPerSession || 45} min</span>
                    <span className="px-2 py-1 rounded bg-enf-surface-elevated border border-enf-surface-border text-enf-text-secondary">Tier 1{tierLimit >= 2 ? '+2' : ''}{tierLimit >= 3 ? '+3' : ''}</span>
                    <span className="px-2 py-1 rounded bg-enf-surface-elevated border border-enf-surface-border text-enf-text-secondary">{todayBlocks.length} exercises</span>
                  </div>
                </div>
              </div>
              <div className="p-6 flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-wrap gap-2">
                  {(todayTemplate?.muscles || sampleWorkout.muscles).map((m) => (
                    <span key={m} className="px-3 py-1 rounded-full bg-enf-surface-mid border border-enf-surface-border text-xs text-enf-text-secondary">{m}</span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => (isPaid ? setAiModalOpen(true) : setAiPaywallOpen(true))}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-enf-accent/10 hover:bg-enf-accent border border-enf-accent-fg/40 hover:border-enf-accent-fg text-enf-accent-fg hover:text-enf-accent-contrast rounded-md font-semibold text-sm transition-colors"
                    data-testid="dashboard-ai-workout-button"
                  >
                    <Sparkles className="w-4 h-4" /> Ignite a Custom Day
                  </button>
                  <Link to="/strength" className="inline-flex items-center gap-2 px-5 py-2.5 bg-enf-accent hover:bg-enf-accent-hover text-enf-accent-contrast rounded-md font-semibold text-sm transition-colors">
                    Start workout <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Empty-state Muscle Recovery card */}
          <WidgetGate page="dashboard" id="muscleRecovery">
            <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="text-sm font-semibold uppercase tracking-widest text-enf-text-secondary">Muscle recovery</div>
                <Activity className="w-4 h-4 text-enf-text-muted" />
              </div>
              {history.length === 0 ? (
                <div className="text-sm text-enf-text-muted leading-relaxed">
                  We'll start tracking muscle recovery once you log your first session. Each completed workout flags which muscle groups are fatigued, and the bars below will show recovery progress in real time.
                </div>
              ) : (
                <div className="space-y-3 text-sm text-enf-text-muted">
                  Recovery model wakes up once we have enough recent volume to estimate fatigue. Keep training — meaningful values usually appear after 3-5 logged sessions.
                </div>
              )}
            </div>
          </WidgetGate>

          {/* Today's fuel — nutrition snapshot */}
          <WidgetGate page="dashboard" id="todaysFuel" className="lg:col-span-3">
            {isPaid ? (
            <Link to="/nutrition" className="group bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6 hover:border-enf-accent-fg/50 transition-colors block" data-testid="dashboard-nutrition-tile">
            <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-8">
              <div className="flex items-start gap-4 md:w-72 flex-shrink-0">
                <div className="w-10 h-10 rounded-md bg-enf-surface-mid flex items-center justify-center flex-shrink-0">
                  <Utensils className="w-5 h-5 text-enf-accent-fg" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-[0.3em] text-enf-role-info font-semibold mb-1">Today's fuel</div>
                  {nutritionTargets ? (
                    <>
                      <div className="font-display text-4xl tracking-wide leading-none">
                        {Math.round(nutritionTotals.kcal)}<span className="text-enf-text-muted text-base ml-1">/ {nutritionTargets.calories} kcal</span>
                      </div>
                      <div className="text-xs text-enf-text-muted mt-1">
                        {Math.max(0, nutritionTargets.calories - Math.round(nutritionTotals.kcal))} remaining · {nutritionTargets.goal.name}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="font-display text-3xl tracking-wide leading-none">Set your targets</div>
                      <div className="text-xs text-enf-text-muted mt-1">Add a few profile basics — Enferno does the math.</div>
                    </>
                  )}
                </div>
              </div>

              {nutritionTargets && (
                <div className="flex-1 grid grid-cols-3 gap-4">
                  {[
                    { label: 'Protein', cur: Math.round(nutritionTotals.protein), max: nutritionTargets.proteinG, pct: proteinPct, color: 'bg-enf-accent' },
                    { label: 'Carbs',   cur: Math.round(nutritionTotals.carbs),   max: nutritionTargets.carbG,    pct: carbsPct,   color: 'bg-enf-role-info' },
                    { label: 'Fat',     cur: Math.round(nutritionTotals.fat),     max: nutritionTargets.fatG,     pct: fatPct,     color: 'bg-amber-500' },
                  ].map((m) => (
                    <div key={m.label}>
                      <div className="flex items-baseline justify-between mb-1.5">
                        <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold">{m.label}</div>
                        <div className="text-xs text-enf-text-secondary"><span className="font-bold">{m.cur}</span><span className="text-enf-text-muted">/{m.max}g</span></div>
                      </div>
                      <div className="h-1.5 bg-enf-surface-mid rounded-full overflow-hidden">
                        <div className={`h-full ${m.color} rounded-full transition-all duration-500`} style={{ width: `${m.pct}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="inline-flex items-center gap-2 px-4 py-2 bg-enf-accent/10 border border-enf-accent-fg/30 group-hover:bg-enf-accent group-hover:border-enf-accent-fg rounded-md text-sm font-semibold transition-colors flex-shrink-0">
                {nutritionTargets ? (kcalPct === 0 ? 'Log food' : 'Open nutrition') : 'Set up'} <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </Link>
            ) : (
              <button
                type="button"
                onClick={() => setNutritionPaywallOpen(true)}
                className="group w-full text-left bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6 hover:border-enf-accent-fg/40 transition-colors block relative overflow-hidden"
                data-testid="dashboard-nutrition-tile-locked"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-md bg-enf-surface-mid flex items-center justify-center flex-shrink-0">
                    <Utensils className="w-5 h-5 text-enf-accent-fg" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="text-xs uppercase tracking-[0.3em] text-enf-role-info font-semibold">Today's fuel</div>
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-widest font-bold bg-enf-accent/10 text-enf-accent-fg border border-enf-accent-fg/30">
                        <Lock className="w-2.5 h-2.5" /> Enferno
                      </span>
                    </div>
                    <div className="font-display text-2xl tracking-wide leading-none mb-1">Unlock Nutrition</div>
                    <div className="text-xs text-enf-text-muted">Macros, calorie targets, Food Library, and meal logging — all in Enferno.</div>
                  </div>
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-enf-accent/10 border border-enf-accent-fg/30 group-hover:bg-enf-accent group-hover:border-enf-accent-fg rounded-md text-sm font-semibold transition-colors flex-shrink-0">
                    See pricing <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </button>
            )}
          </WidgetGate>

          {/* Macro Pattern — last 7 days, anti-shame "look at the shape" view */}
          {isPaid && (
          <WidgetGate page="dashboard" id="macroPattern" className="lg:col-span-3">
            <MacroPattern profile={profile} />
          </WidgetGate>
          )}

          {/* Weekly chart — full-width with prior-week + 4wk avg overlay */}
          <WidgetGate page="dashboard" id="weeklyChart" className="lg:col-span-3">
            <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <div>
                <div className="text-sm font-semibold uppercase tracking-widest text-enf-text-secondary mb-1">This week</div>
                <div className="flex items-baseline gap-3">
                  <div className="font-display text-4xl tracking-wide leading-none">{totalWeekMinutes} min</div>
                  {trendStats.lastWeek > 0 && (
                    <div className={`text-xs font-semibold ${
                      totalWeekMinutes >= trendStats.lastWeek ? 'text-enf-role-info' : 'text-enf-role-warning'
                    }`}>
                      {totalWeekMinutes >= trendStats.lastWeek ? '▲' : '▼'} {Math.abs(totalWeekMinutes - trendStats.lastWeek)} min vs last week
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-enf-accent" /> Strength</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-enf-role-info" /> Cardio</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-enf-role-warning" /> Combatives</span>
                {trendStats.avg4wk > 0 && (
                  <span className="flex items-center gap-1.5 text-enf-text-muted"><span className="w-3 h-px bg-enf-text-muted" /> 4-wk avg ({trendStats.avg4wk} min/wk)</span>
                )}
                {trendStats.lastWeek > 0 && (
                  <span className="flex items-center gap-1.5 text-enf-text-muted"><span className="w-3 h-px border-t border-dashed border-enf-surface-border" /> Last week ({trendStats.lastWeek} min)</span>
                )}
              </div>
            </div>
            {totalWeekMinutes === 0 && trendStats.lastWeek === 0 ? (
              <div className="h-56 flex items-center justify-center text-center">
                <div>
                  <Activity className="w-6 h-6 mx-auto mb-2 text-enf-text-muted" />
                  <div className="text-sm text-enf-text-muted">No sessions logged this week yet.</div>
                  <Link to="/strength" className="inline-block mt-3 text-xs text-enf-role-info hover:text-enf-text-primary">Start today's workout →</Link>
                </div>
              </div>
            ) : (() => {
              // Chart scale uses the larger of (max daily total, prior-week avg-per-day, 4wk-avg-per-day)
              const lastWeekAvgPerDay = trendStats.lastWeek / 7;
              const avg4wkPerDay = trendStats.avg4wk / 7;
              const chartMax = Math.max(60, ...weeklyBars.map((d) => d.strength + d.cardio + d.combatives), lastWeekAvgPerDay, avg4wkPerDay);
              const lastWeekLineHeight = (lastWeekAvgPerDay / chartMax) * 100;
              const avg4wkLineHeight = (avg4wkPerDay / chartMax) * 100;
              return (
                <div className="relative h-56">
                  {/* Overlay reference lines */}
                  {trendStats.lastWeek > 0 && (
                    <div className="absolute left-0 right-0 border-t border-dashed border-enf-surface-border/60 pointer-events-none z-10"
                      style={{ bottom: `${lastWeekLineHeight}%` }} />
                  )}
                  {trendStats.avg4wk > 0 && (
                    <div className="absolute left-0 right-0 border-t border-enf-surface-border/40 pointer-events-none z-10"
                      style={{ bottom: `${avg4wkLineHeight}%` }} />
                  )}
                  <div className="absolute inset-0 flex items-end gap-3">
                    {weeklyBars.map((d) => {
                      const total = d.strength + d.cardio + d.combatives;
                      const sH = (d.strength / chartMax) * 100;
                      const cH = (d.cardio / chartMax) * 100;
                      const kH = (d.combatives / chartMax) * 100;
                      return (
                        <div key={d.day} className="flex-1 flex flex-col items-center gap-2 h-full">
                          <div className="flex-1 w-full flex flex-col justify-end gap-0.5">
                            {d.combatives > 0 && <div className="w-full bg-enf-role-warning rounded-sm" style={{ height: `${kH}%` }} title={`${d.combatives} min combatives`} />}
                            {d.cardio > 0 && <div className="w-full bg-enf-role-info rounded-sm" style={{ height: `${cH}%` }} title={`${d.cardio} min cardio`} />}
                            {d.strength > 0 && <div className="w-full bg-enf-accent rounded-sm" style={{ height: `${sH}%` }} title={`${d.strength} min strength`} />}
                            {total === 0 && <div className="w-full h-1 bg-enf-surface-mid rounded-sm" />}
                          </div>
                          <div className="text-xs text-enf-text-muted">{d.day}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
            </div>
          </WidgetGate>

          {/* Workout history */}
          {history.filter(isStrengthWorkout).length > 0 && (
            <WidgetGate page="dashboard" id="recentWorkouts" className="lg:col-span-3">
              <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-sm font-semibold uppercase tracking-widest text-enf-text-secondary">Recent workouts</div>
                  <span className="text-xs text-enf-text-muted">{totalStrength} logged</span>
                </div>
                <div className="grid md:grid-cols-3 gap-3">
                  {history.filter(isStrengthWorkout).slice(0, 6).map((h) => (
                    <div key={h.id} className="relative bg-enf-surface-mid border border-enf-surface-border rounded-lg p-4 group">
                      <button
                        onClick={() => setEditingEntry(h)}
                        className="absolute top-2 right-2 p-1 rounded-md text-enf-text-muted hover:text-enf-text-primary hover:bg-enf-surface-elevated opacity-60 group-hover:opacity-100 transition-opacity"
                        title="Edit or delete this workout"
                        data-testid={`strength-entry-edit-${h.id}`}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      <div className="text-xs text-enf-role-info uppercase tracking-widest mb-1 flex items-center gap-1.5 pr-7">
                        <span>{new Date(h.date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                        {h.offline_queued && (
                          <span
                            className="px-1 py-0.5 rounded bg-amber-500/15 text-amber-300 text-[9px] font-semibold tracking-widest"
                            title="Logged offline — synced when you reconnected"
                            data-testid="history-offline-badge"
                          >
                            OFFLINE
                          </span>
                        )}
                      </div>
                      <div className="font-display text-2xl tracking-wide leading-none mb-2">{h.title}</div>
                      <div className="flex flex-wrap gap-3 text-xs text-enf-text-secondary">
                        <span>{h.exercisesCompleted ?? 0}/{h.exerciseCount ?? 0} done</span>
                        <span>{(h.totalVolume ?? 0).toLocaleString()} lb</span>
                        <span>{h.duration ?? 0} min</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </WidgetGate>
          )}

          {/* Recent Combatives sessions — own section so the martial-arts
              practice signal doesn't get lost amid strength workouts. Linked
              to /training for the full session log.. */}
          {combativesSessions.length > 0 && (
            <WidgetGate page="dashboard" id="recentCombatives" className="lg:col-span-3">
              <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6" data-testid="combatives-recent">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-sm font-semibold uppercase tracking-widest text-enf-text-secondary">Recent Combatives</div>
                  <Link to="/training" className="text-xs text-enf-role-info hover:text-enf-text-primary transition-colors">
                    {combativesSessions.length} logged · view all →
                  </Link>
                </div>
                <div className="grid md:grid-cols-3 gap-3">
                  {combativesSessions.slice(0, 6).map((s) => (
                    <Link
                      key={s.id}
                      to="/training"
                      className="bg-enf-surface-mid border border-enf-surface-border hover:border-enf-role-warning/40 rounded-lg p-4 transition-colors block"
                      data-testid={`combatives-recent-${s.id}`}
                    >
                      <div className="text-xs text-enf-role-warning uppercase tracking-widest mb-1">
                        {new Date(s.date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                      </div>
                      <div className="font-display text-2xl tracking-wide leading-none mb-2">{s.artName || s.art || 'Session'}</div>
                      <div className="flex flex-wrap gap-3 text-xs text-enf-text-secondary">
                        {s.focus && <span>{s.focus}</span>}
                        <span>{s.duration ?? 0} min</span>
                      </div>
                    </Link>
                  ))}
                </div>
                {combativesThisWeek > 0 && (
                  <div className="mt-4 text-xs text-enf-text-muted">
                    {combativesThisWeek} min this week
                  </div>
                )}
              </div>
            </WidgetGate>
          )}
        </div>

        {/* Modules you're not currently tracking — quiet, collapsible footer */}
        <NotTrackingModulesFooter profile={profile} />
      </div>
      <AIWorkoutModal
        open={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        focus={todayTemplate?.title || sampleWorkout.title || 'Full Body'}
      />
      <StrengthEntryEditModal
        entry={editingEntry}
        open={!!editingEntry}
        onClose={() => setEditingEntry(null)}
        onSave={(patch) => updateWorkout(editingEntry.id, patch)}
        onDelete={() => removeWorkout(editingEntry.id)}
      />
      <WelcomeTour />
      <PaywallModal open={nutritionPaywallOpen} onOpenChange={setNutritionPaywallOpen} feature="nutrition" />
      <PaywallModal open={aiPaywallOpen} onOpenChange={setAiPaywallOpen} feature="ai_workouts" />
    </div>
  );
};

export default Dashboard;

// ───────────────────────────────────────────────────────────────────
// NotTrackingModulesFooter — collapsed-by-default reminder that hidden
// modules (Recovery / Nutrition / Combatives / Library / Coach) are
// still one tap away. Visual impact: 1 collapsed row + an open list
// only when expanded. Doesn't crowd the Progress page.
// ───────────────────────────────────────────────────────────────────
const MODULE_META = {
  recovery:   { label: 'Endurance',  to: '/endurance', icon: HeartIcon,     blurb: 'Walk/run, cycling, hiking, swimming, etc.' },
  nutrition:  { label: 'Nutrition',  to: '/nutrition', icon: UtensilsIcon,  blurb: 'Calorie + macro tracking.' },
  training:   { label: 'Combatives', to: '/training',  icon: Swords,        blurb: 'Martial arts tracking with ranks.' },
  library:    { label: 'Library',    to: '/library',   icon: BookOpenIcon,  blurb: 'Exercise library + The Vault gear directory.' },
  coach:      { label: 'Coach',      to: '/coach',     icon: SparklesIcon,  blurb: 'Ask Enferno AI conversations.' },
};

const NotTrackingModulesFooter = ({ profile }) => {
  const [open, setOpen] = React.useState(false);
  const { enabled: coachEnabled } = useFeatureFlag('ai_coach');
  // Same posture as Navbar: Coach is hidden unless the global flag is ON,
  // regardless of the user's individual tracking preferences.
  const hidden = Object.keys(MODULE_META)
    .filter((k) => !isModuleVisible(k, profile))
    .filter((k) => !(k === 'coach' && !coachEnabled));
  if (hidden.length === 0) return null;

  return (
    <div className="mt-10 pt-6 border-t border-enf-surface-border" data-testid="not-tracking-modules">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-xs text-enf-text-muted hover:text-enf-role-info transition-colors flex items-center gap-2"
        aria-expanded={open}
        data-testid="not-tracking-toggle"
      >
        <span>{open ? '▼' : '▶'}</span> Modules you're not currently tracking ({hidden.length})
      </button>
      {open && (
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {hidden.map((k) => {
            const meta = MODULE_META[k];
            const Icon = meta.icon;
            return (
              <Link
                key={k}
                to={meta.to}
                className="flex items-center gap-3 px-3 py-2.5 bg-enf-surface-deep border border-enf-surface-border hover:border-enf-role-info/40 rounded-md transition-colors group"
                data-testid={`activate-module-${k}`}
              >
                <Icon className="w-4 h-4 text-enf-text-muted group-hover:text-enf-role-info flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-enf-text-secondary truncate">{meta.label}</div>
                  <div className="text-[10px] text-enf-text-muted truncate">Tap to activate</div>
                </div>
                <ArrowUpRight className="w-3 h-3 text-enf-text-muted group-hover:text-enf-role-info flex-shrink-0" />
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};
